import type { HTMLAttributes } from "react";
import { cn } from "../../lib/cn";
import {
  NO_DATA_DASH,
  formatCurrency,
  formatNumber,
  formatPercent,
} from "../../lib/format";

export type MetricFormat = "number" | "percent" | "currency";

export interface MetricValueProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /**
   * `null` / `undefined` = SEM DADOS (não houve medição) — sai `—`.
   * `0` = zero medido, valor válido — sai `0`.
   * Essa diferença é invariante do produto, não detalhe de UI.
   */
  value: number | null | undefined;
  format?: MetricFormat;
  /** Casas decimais. Percentual usa 1 por padrão, número usa 0. */
  decimals?: number;
  /**
   * `"auto"` abrevia só a partir de 10.000. Use `"never"` em contagem
   * crítica (total de leads do dia) — abreviar ali esconde informação.
   */
  abbreviate?: "auto" | "never";
  currency?: string;
  /** Prefixo/sufixo coladinho (ex.: "R$", "/dia"). */
  suffix?: string;
  /** Texto lido no lugar do travessão. */
  noDataLabel?: string;
}

/**
 * Número formatado em pt-BR com `tabular-nums` — sem isso o dígito muda de
 * largura a cada poll e a tela "treme" (seção 2).
 * Não calcula nada: recebe o número pronto do servidor.
 */
export function MetricValue({
  value,
  format = "number",
  decimals,
  abbreviate = "auto",
  currency = "BRL",
  suffix,
  noDataLabel = "sem dados neste período",
  className,
  ...props
}: MetricValueProps) {
  const hasData = value !== null && value !== undefined && Number.isFinite(value);

  if (!hasData) {
    return (
      <span className={cn("nums text-subtle-foreground", className)} {...props}>
        <span aria-hidden="true">{NO_DATA_DASH}</span>
        <span className="sr-only">{noDataLabel}</span>
      </span>
    );
  }

  const numeric = value as number;
  const text =
    format === "percent"
      ? formatPercent(numeric, decimals ?? 1)
      : format === "currency"
        ? formatCurrency(numeric, currency, decimals ?? 2)
        : formatNumber(numeric, { ...(decimals !== undefined ? { decimals } : {}), abbreviate });

  return (
    <span className={cn("nums", className)} {...props}>
      {text}
      {/* sufixo menor e apagado: a unidade acompanha o número sem disputar com ele */}
      {suffix ? (
        <span className="ml-0.5 text-[0.6em] font-medium text-subtle-foreground">{suffix}</span>
      ) : null}
    </span>
  );
}
